import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import {
  Users, GraduationCap, ClipboardList, PhoneOutgoing, BarChart3, BookOpen,
  Video, ChevronRight, AlertTriangle, Cake, TrendingUp, ShieldCheck, CalendarDays, UserCheck,
  Rocket, RotateCcw, PhoneCall, Download, FileText, HeartPulse, Trophy, ClipboardCheck,
} from "lucide-react";
const clsLabel = (c) => (["LKG", "UKG", "Play Group"].includes(c) ? c : `${c}`);
const pctColor = (p) => (p < 50 ? "text-rose-600 dark:text-rose-400" : p <= 70 ? "text-sky-600 dark:text-sky-400" : "text-emerald-600 dark:text-emerald-400");
import BroadcastManager from "@/components/BroadcastManager";
import NextUpBanner from "@/components/NextUpBanner";
import { WeakSubjectsModal } from "@/components/WeakSubjectsModal";
import { AttendanceTodayModal } from "@/components/AttendanceTodayModal";
import { PracticeTestsModal } from "@/components/PracticeTestsModal";

async function exportReport(days, fmt) {
  try {
    const res = await api.get("/admin/outreach-report", {
      params: { days, format: fmt }, responseType: "blob",
    });
    const url = window.URL.createObjectURL(new Blob([res.data]));
    const a = document.createElement("a");
    a.href = url;
    a.download = `outreach-report-${days}d.${fmt}`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  } catch {/* silent */}
}

function todayISO() {
  const d = new Date();
  return new Date(d.getTime() - d.getTimezoneOffset() * 60_000).toISOString().slice(0, 10);
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  // TTS health banner — fetched every 2 mins; visible only when Sarvam
  // streaming has been falling back to OpenAI TTS in the last 24h.
  const [ttsHealth, setTtsHealth] = useState(null);
  const [outreach, setOutreach] = useState(null);
  const [outreachDays, setOutreachDays] = useState(7);
  const [weakOpen, setWeakOpen] = useState(false);
  const [attOpen, setAttOpen] = useState(false);
  const [ptOpen, setPtOpen] = useState(false);
  const [staff, setStaff] = useState(null);
  const [staffLoading, setStaffLoading] = useState(true);
  const [weakTop, setWeakTop] = useState(null);

  useEffect(() => {
    api.get("/admin/marks/weak-subjects")
      .then(({ data }) => setWeakTop((data.rows || []).slice(0, 3)))
      .catch(() => setWeakTop([]));
  }, []);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await api.get("/staff/today");
        if (!cancelled) setStaff(data);
      } catch {
        if (!cancelled) setStaff(null);
      } finally {
        if (!cancelled) setStaffLoading(false);
      }
    };
    load();
    const id = setInterval(load, 60_000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setOutreach(null);
    api.get("/admin/outreach-summary", { params: { days: outreachDays } })
      .then(({ data }) => { if (!cancelled) setOutreach(data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [outreachDays]);

  useEffect(() => {
    let cancelled = false;
    const load = async (isInitial) => {
      if (isInitial) setLoading(true);
      try {
        const { data } = await api.get("/admin/stats/overview");
        if (!cancelled) setStats(data);
      } catch {
        if (!cancelled) setStats(null);
      } finally {
        if (!cancelled && isInitial) setLoading(false);
      }
    };
    load(true);
    const id = setInterval(() => load(false), 60_000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await api.get("/admin/tts-health");
        if (!cancelled) setTtsHealth(data);
      } catch {/* silent — no banner if endpoint hiccups */}
    };
    load();
    const id = setInterval(load, 120_000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  const today = todayISO();
  const attPct = stats?.attendance_today?.percentage ?? 0;
  const attTone = attPct >= 90 ? "emerald" : attPct >= 75 ? "sky" : "rose";
  const markedClasses = stats?.attendance_today?.marked_classes ?? 0;
  const totalClasses = stats?.attendance_today?.total_possible_classes ?? 0;
  const atRisk = stats?.analytics_30d?.at_risk_count ?? 0;

  return (
    <Layout>
      <div className="mb-8 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-slate-400 dark:text-slate-500 mb-2">Admin dashboard</div>
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 dark:text-slate-100" style={{ fontFamily: "Outfit" }}>
            School overview
          </h1>
          <p className="text-slate-500 dark:text-slate-400 mt-1">{today.split("-").reverse().join("-")} · Refreshes every minute</p>
        </div>
        <div className="flex flex-wrap items-center justify-end gap-2.5">
          <BroadcastManager />
          <NextUpBanner holidayEndpoint="/holidays" holidayTo="/holidays" eventEndpoint="/events" eventTo="/events" />
        </div>
      </div>

      {ttsHealth && ttsHealth.last_24h_failures > 0 && user?.is_dev_admin && (
        <div
          className="mb-6 flex items-start gap-3 rounded-2xl border border-amber-300/70 dark:border-amber-700/60 bg-amber-50 dark:bg-amber-950/40 px-4 py-3"
          data-testid="admin-tts-fallback-banner"
        >
          <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-300 shrink-0 mt-0.5" />
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold text-amber-800 dark:text-amber-200">
              AI Tutor voice fell back to OpenAI {ttsHealth.last_24h_failures}× in the last 24h
            </div>
            <div className="text-xs text-amber-700/90 dark:text-amber-300/80 mt-0.5">
              Sarvam streaming TTS is failing. Check the <code className="font-mono">SARVAM_API_KEY</code>, quota, or service status. Students still hear audio via the OpenAI fallback voice.
            </div>
          </div>
        </div>
      )}

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="admin-overview-cards">
        {/* Teachers — staff attendance today */}
        <DashCard
          testid="admin-card-teachers"
          tone="sky"
          icon={Users}
          title="Teachers"
          subtitle="Staff today"
          onClick={() => navigate("/admin/teachers")}
        >
          {staffLoading ? <Skel /> : (
            <div className="space-y-2.5">
              <div className="flex items-baseline gap-5">
                <div>
                  <div className="text-3xl font-bold text-emerald-600 dark:text-emerald-300" style={{ fontFamily: "Outfit" }} data-testid="admin-teachers-in-college">
                    {staff?.in_college_count ?? 0}
                  </div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-400">In school</div>
                </div>
                <div className="h-9 w-px bg-slate-200 dark:bg-slate-700" />
                <div>
                  <div className="text-3xl font-bold text-amber-600 dark:text-amber-300" style={{ fontFamily: "Outfit" }} data-testid="admin-teachers-on-leave">
                    {staff?.on_leave_count ?? 0}
                  </div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-400">On leave</div>
                </div>
              </div>
              <div className="text-xs text-slate-500 dark:text-slate-400">
                {(staff?.on_leave_count ?? 0) === 0
                  ? "All teachers are in school today. No leaves to cover."
                  : `${staff.on_leave_count} on leave${(staff?.risks?.length ?? 0) > 0 ? ` · ${staff.risks.length} class(es) need a substitute` : ""}`}
              </div>
            </div>
          )}
        </DashCard>

        {/* Marks analytics */}
        <DashCard
          testid="admin-card-marks-analytics"
          tone="indigo"
          icon={BarChart3}
          title="Marks analytics"
          subtitle="Exam performance"
          onClick={() => setWeakOpen(true)}
        >
          <div className="space-y-2" data-testid="marks-weak-top">
            <div className="text-[10px] uppercase tracking-[0.15em] text-slate-400">Lowest-scoring subjects</div>
            {weakTop == null ? (
              <Skel />
            ) : weakTop.length === 0 ? (
              <div className="text-xs text-slate-500 dark:text-slate-400">No published results yet.</div>
            ) : (
              <ul className="space-y-1">
                {weakTop.map((r, i) => (
                  <li key={`${r.class_name}-${r.section}-${r.subject}-${i}`} className="flex items-center justify-between gap-2 text-sm">
                    <span className="truncate text-slate-700 dark:text-slate-200">
                      {r.subject} <span className="text-slate-400 text-xs">· {clsLabel(r.class_name)}-{r.section}</span>
                    </span>
                    <span className={`font-bold ${pctColor(r.avg_pct)}`}>{r.avg_pct}%</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </DashCard>

        {/* Today's attendance (school-wide) */}
        <DashCard
          testid="admin-card-attendance-today"
          tone={attTone}
          icon={ClipboardList}
          title="Today's attendance"
          subtitle="School-wide"
          onClick={() => setAttOpen(true)}
        >
          {loading ? <Skel /> : (
            <div className="space-y-2">
              <div className={`text-3xl font-bold ${attTone === "emerald" ? "text-emerald-600 dark:text-emerald-300" : attTone === "sky" ? "text-sky-600 dark:text-sky-300" : "text-rose-600 dark:text-rose-300"}`} style={{ fontFamily: "Outfit" }}>
                {attPct}%
              </div>
              <div className="text-xs text-slate-500 dark:text-slate-400">
                {markedClasses}/{totalClasses} classes marked · {stats?.attendance_today?.present ?? 0} present · <span className="font-semibold text-rose-600 dark:text-rose-400" data-testid="attendance-absent-count">{stats?.attendance_today?.absent ?? 0} absent</span>
              </div>
            </div>
          )}
        </DashCard>

        {/* Students */}
        <DashCard
          testid="admin-card-students"
          tone="emerald"
          icon={GraduationCap}
          title="Students"
          subtitle="Enrolment"
          onClick={() => navigate("/students")}
        >
          {loading ? <Skel /> : (
            <div className="space-y-2">
              <div className="text-3xl font-bold text-emerald-600 dark:text-emerald-300" style={{ fontFamily: "Outfit" }}>
                {stats?.students?.total ?? 0}
              </div>
              {stats?.students?.largest_classes?.length > 0 && (
                <div className="text-xs text-slate-500 dark:text-slate-400 truncate">
                  Largest: {stats.students.largest_classes.slice(0, 3).map((c) => `${c.class_name}-${c.section} (${c.count})`).join(" · ")}
                </div>
              )}
            </div>
          )}
        </DashCard>

        {/* Practice tests (teacher-created) */}
        <DashCard
          testid="admin-card-practice-tests"
          tone="sky"
          icon={ClipboardCheck}
          title="Practice Tests"
          subtitle="Given by teachers"
          onClick={() => setPtOpen(true)}
        >
          {loading ? <Skel /> : (
            <div className="space-y-2">
              <div className="text-3xl font-bold text-sky-600 dark:text-sky-300" style={{ fontFamily: "Outfit" }} data-testid="practice-tests-today">
                {stats?.practice_tests?.today_count ?? 0}
                <span className="text-xs font-normal text-slate-400 ml-1.5">given today</span>
              </div>
              {(() => {
                const noTest = stats?.practice_tests?.classes_without_tests ?? [];
                if (noTest.length === 0) {
                  return (
                    <div className="text-xs inline-flex items-center gap-1 text-emerald-600 dark:text-emerald-300">
                      <ShieldCheck className="h-3 w-3" /> All classes have tests
                    </div>
                  );
                }
                return (
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.15em] text-slate-400 mb-1">No tests yet ({noTest.length})</div>
                    <div className="flex flex-wrap items-center gap-1.5" data-testid="practice-tests-no-test">
                      {noTest.slice(0, 6).map((c) => (
                        <Pill key={`${c.class_name}-${c.section}`} tone="rose" icon={AlertTriangle} label={`${clsLabel(c.class_name)}-${c.section}`} />
                      ))}
                    </div>
                  </div>
                );
              })()}
            </div>
          )}
        </DashCard>

        {/* At-risk students */}
        <DashCard
          testid="admin-card-at-risk"
          tone="amber"
          icon={AlertTriangle}
          title="At-risk students"
          subtitle="<75% in last 30 days"
          onClick={() => navigate("/analytics")}
        >
          {loading ? <Skel /> : (
            <div className="space-y-2">
              <div className={`text-3xl font-bold ${atRisk > 0 ? "text-amber-600 dark:text-amber-300" : "text-emerald-600 dark:text-emerald-300"}`} style={{ fontFamily: "Outfit" }}>
                {atRisk}
              </div>
              <div className="text-xs text-slate-500 dark:text-slate-400 flex items-center gap-1">
                {atRisk > 0 ? (
                  <span>Across {stats?.analytics_30d?.students_with_records ?? 0} tracked students</span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-emerald-600 dark:text-emerald-300">
                    <ShieldCheck className="h-3 w-3" /> All clear
                  </span>
                )}
              </div>
            </div>
          )}
        </DashCard>

        {/* Voice calls today */}
        <DashCard
          testid="admin-card-voice-calls"
          tone="rose"
          icon={PhoneOutgoing}
          title="Voice calls today"
          subtitle="Absent + birthday"
          onClick={() => navigate("/voice-calls")}
        >
          {loading ? <Skel /> : (
            <div className="space-y-2">
              <div className="text-3xl font-bold text-rose-600 dark:text-rose-300" style={{ fontFamily: "Outfit" }}>
                {stats?.voice_calls_today?.total ?? 0}
              </div>
              <div className="flex flex-wrap items-center gap-1.5">
                <Pill tone="rose" icon={PhoneOutgoing} label={`${stats?.voice_calls_today?.absent ?? 0} absent`} />
                <Pill tone="pink" icon={Cake} label={`${stats?.voice_calls_today?.birthday ?? 0} birthday`} />
                {(stats?.voice_calls_today?.failed ?? 0) > 0 && (
                  <Pill tone="amber" icon={AlertTriangle} label={`${stats.voice_calls_today.failed} failed`} />
                )}
              </div>
            </div>
          )}
        </DashCard>

        {/* Homework + Online classes */}
        <DashCard
          testid="admin-card-homework"
          tone="violet"
          icon={BookOpen}
          title="Homework"
          subtitle="Assigned today"
          onClick={() => navigate("/homework")}
        >
          {loading ? <Skel /> : (
            <div className="space-y-2">
              <div className="text-3xl font-bold text-violet-600 dark:text-violet-300" style={{ fontFamily: "Outfit" }}>
                {stats?.homework_today_count ?? 0}
              </div>
              <div className="text-xs text-slate-500 dark:text-slate-400 inline-flex items-center gap-1.5">
                <Video className="h-3 w-3 text-emerald-500" /> {stats?.online_classes_today_count ?? 0} online classes scheduled
              </div>
            </div>
          )}
        </DashCard>
      </div>
      <OutreachPanel data={outreach} days={outreachDays} onDaysChange={setOutreachDays} onClick={() => navigate("/voice-calls")} />

      {/* Quick action shortcuts */}
      <div className="mt-8 rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-5">
        <div className="text-xs uppercase tracking-[0.2em] text-slate-400 dark:text-slate-500 mb-3">Quick actions</div>
        <div className="flex flex-wrap gap-2">
          <QuickBtn onClick={() => navigate("/admin/students")} label="Import students" icon={GraduationCap} testid="admin-quick-import" />
          <QuickBtn onClick={() => navigate("/admin/teachers")} label="Add teacher" icon={Users} testid="admin-quick-teacher" />
          <QuickBtn onClick={() => navigate("/voice-calls")} label="Birthday call settings" icon={Cake} testid="admin-quick-birthday" />
          <QuickBtn onClick={() => navigate("/analytics")} label="Open analytics" icon={TrendingUp} testid="admin-quick-analytics" />
        </div>
      </div>

      <WeakSubjectsModal open={weakOpen} onOpenChange={setWeakOpen} />
      <AttendanceTodayModal open={attOpen} onOpenChange={setAttOpen} />
      <PracticeTestsModal open={ptOpen} onOpenChange={setPtOpen} />
    </Layout>
  );
}

function DashCard({ children, onClick, testid, tone, icon: Icon, title, subtitle }) {
  const tones = {
    sky: "border-sky-200 dark:border-sky-800 hover:border-sky-300",
    amber: "border-amber-200 dark:border-amber-800 hover:border-amber-300",
    violet: "border-violet-200 dark:border-violet-800 hover:border-violet-300",
    emerald: "border-emerald-200 dark:border-emerald-800 hover:border-emerald-300",
    rose: "border-rose-200 dark:border-rose-800 hover:border-rose-300",
    pink: "border-pink-200 dark:border-pink-800 hover:border-pink-300",
    indigo: "border-indigo-200 dark:border-indigo-800 hover:border-indigo-300",
    slate: "border-slate-200 dark:border-slate-700 hover:border-slate-300",
  };
  const iconTone = {
    sky: "bg-sky-50 dark:bg-sky-900/40 text-sky-600 dark:text-sky-300",
    amber: "bg-amber-50 dark:bg-amber-900/40 text-amber-600 dark:text-amber-300",
    violet: "bg-violet-50 dark:bg-violet-900/40 text-violet-600 dark:text-violet-300",
    emerald: "bg-emerald-50 dark:bg-emerald-900/40 text-emerald-600 dark:text-emerald-300",
    rose: "bg-rose-50 dark:bg-rose-900/40 text-rose-600 dark:text-rose-300",
    pink: "bg-pink-50 dark:bg-pink-900/40 text-pink-600 dark:text-pink-300",
    indigo: "bg-indigo-50 dark:bg-indigo-900/40 text-indigo-600 dark:text-indigo-300",
    slate: "bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300",
  };
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`text-left bg-white dark:bg-slate-800 rounded-2xl border p-5 transition-all hover:shadow-md hover:-translate-y-0.5 ${tones[tone]}`}
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2.5">
          <div className={`h-10 w-10 rounded-xl flex items-center justify-center ${iconTone[tone]}`}>
            <Icon className="h-5 w-5" />
          </div>
          <div>
            <div className="text-sm font-semibold text-slate-900 dark:text-slate-100" style={{ fontFamily: "Outfit" }}>{title}</div>
            <div className="text-[10px] uppercase tracking-[0.15em] text-slate-400 dark:text-slate-500">{subtitle}</div>
          </div>
        </div>
        <ChevronRight className="h-4 w-4 text-slate-300" />
      </div>
      <div>{children}</div>
    </button>
  );
}

function Pill({ tone, icon: Icon, label }) {
  const cls = {
    sky: "bg-sky-50 dark:bg-sky-900/40 text-sky-700 dark:text-sky-300",
    amber: "bg-amber-50 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300",
    violet: "bg-violet-50 dark:bg-violet-900/40 text-violet-700 dark:text-violet-300",
    rose: "bg-rose-50 dark:bg-rose-900/40 text-rose-700 dark:text-rose-300",
    pink: "bg-pink-50 dark:bg-pink-900/40 text-pink-700 dark:text-pink-300",
    indigo: "bg-indigo-50 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300",
  }[tone];
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-semibold ${cls}`}>
      {Icon && <Icon className="h-3 w-3" />} {label}
    </span>
  );
}

function OutreachPanel({ data, days, onDaysChange, onClick }) {
  const stat = (v) => (v == null ? "—" : v);
  const trend = data?.trend_pct;
  return (
    <div className="mt-8 rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-5" data-testid="admin-outreach-panel">
      <div className="flex items-center justify-between gap-4 mb-4 flex-wrap">
        <div className="flex items-center gap-2.5">
          <div className="h-10 w-10 rounded-xl flex items-center justify-center bg-emerald-50 dark:bg-emerald-900/40 text-emerald-600 dark:text-emerald-300">
            <PhoneCall className="h-5 w-5" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <div className="text-sm font-semibold text-slate-900 dark:text-slate-100" style={{ fontFamily: "Outfit" }}>Parent outreach</div>
              {data && (
                trend == null ? (
                  <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded-full border border-slate-200 bg-slate-50 text-slate-500" data-testid="outreach-trend">new</span>
                ) : (
                  <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full border inline-flex items-center gap-0.5 ${trend > 0 ? "border-emerald-200 bg-emerald-50 text-emerald-700" : trend < 0 ? "border-rose-200 bg-rose-50 text-rose-600" : "border-slate-200 bg-slate-50 text-slate-500"}`} data-testid="outreach-trend" title={`${stat(data?.prev_total_calls)} calls in the previous ${days} days`}>
                    {trend > 0 ? <TrendingUp className="h-3 w-3" /> : trend < 0 ? <AlertTriangle className="h-3 w-3" /> : null}
                    {trend > 0 ? "+" : ""}{trend}% vs prev
                  </span>
                )
              )}
            </div>
            <div className="text-[10px] uppercase tracking-[0.15em] text-slate-400 dark:text-slate-500">Last {days} days</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-full border border-slate-200 dark:border-slate-700 p-0.5" data-testid="outreach-range-toggle">
            {[7, 30].map((d) => (
              <button
                key={d}
                type="button"
                onClick={() => onDaysChange(d)}
                className={`text-[11px] font-semibold px-2.5 py-1 rounded-full transition-colors ${days === d ? "bg-sky-500 text-white" : "text-slate-500 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700"}`}
                data-testid={`outreach-range-${d}`}
              >
                {d}d
              </button>
            ))}
          </div>
          <button type="button" onClick={() => exportReport(days, "csv")} className="text-xs font-semibold text-slate-600 dark:text-slate-300 hover:text-sky-600 inline-flex items-center gap-1 px-2 py-1 rounded-full border border-slate-200 dark:border-slate-700" data-testid="outreach-export-csv" title="Download CSV">
            <Download className="h-3.5 w-3.5" /> CSV
          </button>
          <button type="button" onClick={() => exportReport(days, "pdf")} className="text-xs font-semibold text-slate-600 dark:text-slate-300 hover:text-sky-600 inline-flex items-center gap-1 px-2 py-1 rounded-full border border-slate-200 dark:border-slate-700" data-testid="outreach-export-pdf" title="Download PDF">
            <FileText className="h-3.5 w-3.5" /> PDF
          </button>
          <button type="button" onClick={onClick} className="text-xs font-semibold text-sky-600 dark:text-sky-300 hover:text-sky-700 inline-flex items-center gap-1" data-testid="admin-outreach-view-calls">
            View calls <ChevronRight className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        <OutreachTile icon={Rocket} tone="emerald" label="Congrats calls" value={stat(data?.congrats)} testid="outreach-congrats" />
        <OutreachTile icon={AlertTriangle} tone="rose" label="Decline notices" value={stat(data?.concern)} testid="outreach-concern" />
        <OutreachTile icon={HeartPulse} tone="amber" label="Wellbeing check-ins" value={stat(data?.wellbeing)} testid="outreach-wellbeing" />
        <OutreachTile icon={RotateCcw} tone="violet" label="Auto-retries" value={stat(data?.auto_retries)} testid="outreach-retries" />
        <OutreachTile icon={PhoneCall} tone="sky" label="Picked up" value={data?.pickup_rate == null ? "—" : `${data.pickup_rate}%`} sub={data?.answered_known ? `${data.picked_up}/${data.answered_known} answered` : null} testid="outreach-pickup" />
      </div>
      <div className="mt-3 text-[11px] text-slate-400 dark:text-slate-500">
        {stat(data?.total_calls)} total parent calls · {stat(data?.absent_calls)} absence · {stat(data?.birthday_calls)} birthday
      </div>
    </div>
  );
}

function OutreachTile({ icon: Icon, tone, label, value, sub, testid }) {
  const map = {
    emerald: "bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300",
    rose: "bg-rose-50 dark:bg-rose-900/30 text-rose-700 dark:text-rose-300",
    violet: "bg-violet-50 dark:bg-violet-900/30 text-violet-700 dark:text-violet-300",
    amber: "bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300",
    sky: "bg-sky-50 dark:bg-sky-900/30 text-sky-700 dark:text-sky-300",
  };
  return (
    <div className={`rounded-xl p-3.5 ${map[tone]}`} data-testid={testid}>
      <Icon className="h-4 w-4 mb-2 opacity-80" />
      <div className="text-2xl font-bold" style={{ fontFamily: "Outfit" }}>{value}</div>
      <div className="text-[11px] font-medium opacity-80 mt-0.5">{label}</div>
      {sub && <div className="text-[10px] opacity-60 mt-0.5">{sub}</div>}
    </div>
  );
}


function QuickBtn({ onClick, label, icon: Icon, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-200 text-xs font-medium hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors"
    >
      <Icon className="h-3.5 w-3.5" /> {label}
    </button>
  );
}

function Skel() {
  return <div className="h-10 rounded-lg bg-slate-100 dark:bg-slate-700 animate-pulse" />;
}
